/**
 * imagePrep — shrink lab-result screenshots in the browser and group them into
 * upload batches for upload-labtest.
 *
 * Two limits shape this:
 *
 *   Request size. upload-labtest is a synchronous Lambda, which rejects any
 *   request over 6 MB. Images travel base64-encoded inside JSON (+33%), so a
 *   handful of raw 2-3 MB phone screenshots would hit it. Re-encoding as JPEG
 *   with the width capped brings a screenshot to roughly 150-400 KB.
 *
 *   Legibility. The Lambda does the final sizing for the vision model — fitting
 *   each image under the model's no-resize limit and tiling tall scrolling
 *   screenshots at blank gaps (lambda/upload-labtest/vision_images.py). So the
 *   browser only caps the WIDTH and keeps the full height: shrinking a tall
 *   screenshot to fit here would throw away the resolution tiling needs.
 *
 * Several images selected together go up as one batch, so the model reads them
 * side by side — overlapping screenshots are deduplicated and each result keeps
 * the collection date shown on its own screenshot. A selection too big for one
 * request is split into several; the draws they produce are merged afterwards by
 * the same auto-merge that combines separately uploaded panel sheets.
 */

export const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'heic', 'heif'];

/** Widest an uploaded image is kept; wider than any phone screenshot needs. */
export const MAX_UPLOAD_WIDTH = 1200;
/** Pixel cap so an extreme scroll capture cannot exhaust browser memory. */
export const MAX_UPLOAD_PIXELS = 12_000_000;
export const JPEG_QUALITY = 0.9;

/** Matches MAX_UPLOAD_IMAGES in the Lambda. */
export const MAX_BATCH_IMAGES = 10;
/** Raw bytes per request; base64 makes this ~5.3 MB, under the 6 MB Lambda cap. */
export const MAX_BATCH_BYTES = 4_000_000;

export function isImageFile(file) {
    const ext = (file?.name || '').split('.').pop().toLowerCase();
    return IMAGE_EXTENSIONS.includes(ext) || /^image\//.test(file?.type || '');
}

/**
 * Scale (never above 1) that caps an image's width and total pixel count.
 * Height is otherwise kept, so tall screenshots stay sharp for tiling.
 */
export function uploadScale(width, height) {
    if (!width || !height) return 1;
    return Math.min(1, MAX_UPLOAD_WIDTH / width, Math.sqrt(MAX_UPLOAD_PIXELS / (width * height)));
}

/**
 * Group prepared images into request-sized batches, keeping selection order.
 *
 * @param {Array<{bytes: number}>} images
 * @returns {Array<Array>} batches, each within MAX_BATCH_IMAGES and MAX_BATCH_BYTES
 *   (an image larger than the byte budget on its own still gets a batch)
 */
export function planBatches(images, { maxImages = MAX_BATCH_IMAGES, maxBytes = MAX_BATCH_BYTES } = {}) {
    const batches = [];
    let current = [];
    let currentBytes = 0;
    for (const image of images) {
        const full = current.length >= maxImages || (current.length > 0 && currentBytes + image.bytes > maxBytes);
        if (full) {
            batches.push(current);
            current = [];
            currentBytes = 0;
        }
        current.push(image);
        currentBytes += image.bytes;
    }
    if (current.length) batches.push(current);
    return batches;
}

function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
}

/**
 * Decode, downscale and re-encode one image as JPEG.
 *
 * @returns {Promise<{name: string, b64: string, bytes: number}>}
 * @throws when the browser cannot decode the format (e.g. HEIC outside Safari)
 */
export async function prepareImageForUpload(file) {
    let bitmap;
    try {
        bitmap = await createImageBitmap(file);
    } catch {
        throw new Error(`${file.name}: this browser can't read that image format. Try a PNG or JPEG screenshot.`);
    }
    try {
        const scale = uploadScale(bitmap.width, bitmap.height);
        const width = Math.max(1, Math.round(bitmap.width * scale));
        const height = Math.max(1, Math.round(bitmap.height * scale));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        // JPEG has no alpha: paint transparent screenshot regions white, not black.
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, width, height);
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bitmap, 0, 0, width, height);

        const blob = await new Promise((resolve, reject) =>
            canvas.toBlob(b => (b ? resolve(b) : reject(new Error(`${file.name}: could not encode image`))),
                'image/jpeg', JPEG_QUALITY));
        return { name: file.name, b64: await blobToBase64(blob), bytes: blob.size };
    } finally {
        bitmap.close?.();
    }
}
